import { CreateableMultiSelectProps } from 'app/modules/filters/components/CreatableMultiSelect';
import { JSXElementConstructor } from 'react';
import {
  U21DatePickerProps,
  U21DateRangePickerProps,
  U21NumberRangeProps,
  U21SelectProps,
  U21SliderProps,
  U21TextFieldProps,
} from 'app/shared/u21-ui/components';

import {
  FILTER_OPERATOR,
  FILTER_TYPE,
  SHARING_PRIVATE,
  SHARING_TEAMS,
} from 'app/modules/filters/constants';
import { Unit21DataClassifier } from 'app/modules/dataSettings/responses';

export type FilterType = ValueOf<typeof FILTER_TYPE>;

export type FilterOperator = ValueOf<typeof FILTER_OPERATOR>;

export interface ContainsTextOperatorAndValue {
  operator: 'contains_text';
  value: string;
  onChange: (value: string | undefined) => void;
}

export interface IsAfterDateOperatorAndValue {
  operator: 'is_after_date';
  value: Date;
  onChange: U21DatePickerProps['onChange'];
}

export interface IsBeforeDateOperatorAndValue {
  operator: 'is_before_date';
  value: Date;
  onChange: U21DatePickerProps['onChange'];
}

export interface IsBetweenDateOperatorAndValue {
  operator: 'is_between_date';
  value: [Date, Date];
  onChange: U21DateRangePickerProps['onChange'];
}

export interface IsBetweenNumberOperatorAndValue {
  operator: 'is_between_number';
  value: [number, number];
  onChange: U21NumberRangeProps['onChange'];
}

export interface IsBetweenSliderOperatorAndValue {
  operator: 'is_between_slider';
  value: [number, number];
  onChange: U21SliderProps<[number, number]>['onChange'];
}

export interface IsGreaterNumberOperatorAndValue {
  operator: 'is_greater_than_number';
  value: number;
  onChange: (value: number | undefined) => void;
}

export interface IsLesserNumberOperatorAndValue {
  operator: 'is_less_than_number';
  value: number;
  onChange: (value: number | undefined) => void;
}

export interface IsOneOfOperatorAndValue<
  T extends string | number = string | number,
> {
  operator: 'is_one_of';
  value: T[];
  onChange: (value: T[]) => void;
}

export interface IsNotOneOfOperatorAndValue<
  T extends string | number = string | number,
> {
  operator: 'is_not_one_of';
  value: T[];
  onChange: (value: T[]) => void;
}

export interface IsOperatorAndValue<
  T extends string | number = string | number,
> {
  operator: 'is';
  value: T;
  onChange: (value: T | undefined) => void;
}

export interface IsExactTextOperatorAndValue {
  operator: 'is_exact_text';
  value: string;
  onChange: (value: string | undefined) => void;
}

export interface IsNotExactTextOperatorAndValue {
  operator: 'is_not_exact_text';
  value: string;
  onChange: (value: string | undefined) => void;
}

export interface IsEmptyOperatorAndValue {
  operator: 'is_empty';
  value: undefined;
  onChange: (value: void) => void; // needed for type consistency
}

export interface IsNotEmptyOperatorAndValue {
  operator: 'is_not_empty';
  value: undefined;
  onChange: (value: void) => void; // needed for type consistency
}

export interface IsTrueOperatorAndValue {
  operator: 'is_true';
  value: undefined;
  onChange: (value: void) => void; // needed for type consistency
}

export interface IsNotTrueOperatorAndValue {
  operator: 'is_false';
  value: undefined;
  onChange: (value: void) => void; // needed for type consistency
}

interface BaseFilterOption {
  featureFlag?: string;
  /**
   * If there is a data setting related to this filter option then this `key` should correspond to the `native_key` of that data setting, and you should include the `unit21DataClassifier` in the filter option as well. This is how we dispplay the correct friendly name and description for the filter option.
   * @example "The 'Timestamp' filter for transactions has a corresponding data setting with a native_key of `event_time`; therefore, the key for the 'Timestamp' filter option should be `event_time`."
   */
  key: string;
  label: string;
  permissions?: string[];
  description?: string;
  required?: boolean;
  /**
   * If there is a data setting related to this filter option then this `unit21DataClassifier` should correspond to the `classifier` of that data setting, and you should include the `key` in the filter option as well. This is how we dispplay the correct friendly name and description for the filter option.
   * @example "The 'Timestamp' filter for transactions has a corresponding data setting with a classifier of `Unit21DataClassifier.EVENT`; therefore, the unit21DataClassifier for the 'Timestamp' filter option should be `Unit21DataClassifier.EVENT`."
   * @remarks see {@link Unit21DataClassifier}
   */
  unit21DataClassifier?: Unit21DataClassifier;
}

type CustomizeOption<
  OperatorAndValue extends {
    operator: FilterOperator;
    value: any;
    onChange: (val: unknown, e: unknown) => void;
  },
  InputProps,
> = {
  defaultValue?: OperatorAndValue['value'];
  inputProps?: Omit<InputProps, 'onChange' | 'value'>;
  Display?: JSXElementConstructor<Omit<OperatorAndValue, 'onChange'>>;
  Input?: JSXElementConstructor<OperatorAndValue>;
  disabled?: true;
};

interface BooleanFilterOption {
  type: 'boolean';
  customize?: Record<string, never>;
}

interface DateRangeFilterOption {
  type: 'date_range';
  customize?: {
    [FILTER_OPERATOR.IS_AFTER_DATE]?: CustomizeOption<
      IsAfterDateOperatorAndValue,
      U21DatePickerProps
    >;
    [FILTER_OPERATOR.IS_BEFORE_DATE]?: CustomizeOption<
      IsBeforeDateOperatorAndValue,
      U21DatePickerProps
    >;
    [FILTER_OPERATOR.IS_BETWEEN_DATE]?: CustomizeOption<
      IsBetweenDateOperatorAndValue,
      U21DateRangePickerProps
    >;
  };
}

export interface MultiSelectFilterOption {
  type: 'multi_select';
  customize?: {
    [FILTER_OPERATOR.IS_ONE_OF]?: CustomizeOption<
      IsOneOfOperatorAndValue,
      CreateableMultiSelectProps
    >;
    [FILTER_OPERATOR.IS_NOT_ONE_OF]?: CustomizeOption<
      IsNotOneOfOperatorAndValue,
      CreateableMultiSelectProps
    >;
  };
}

interface NumberRangeFilterOption {
  type: 'number_range';
  customize?: {
    [FILTER_OPERATOR.IS_GREATER_THAN_NUMBER]?: CustomizeOption<
      IsGreaterNumberOperatorAndValue,
      U21TextFieldProps
    >;
    [FILTER_OPERATOR.IS_LESS_THAN_NUMBER]?: CustomizeOption<
      IsLesserNumberOperatorAndValue,
      U21TextFieldProps
    >;
    [FILTER_OPERATOR.IS_BETWEEN_NUMBER]?: CustomizeOption<
      IsBetweenNumberOperatorAndValue,
      U21NumberRangeProps
    >;
  };
}

interface SelectFilterOption {
  type: 'select';
  customize?: {
    [FILTER_OPERATOR.IS]?: CustomizeOption<
      IsOperatorAndValue,
      U21SelectProps<any>
    >;
  };
}

interface SliderRangeFilterOption {
  type: 'slider_range';
  customize?: {
    [FILTER_OPERATOR.IS_BETWEEN_SLIDER]?: CustomizeOption<
      IsBetweenSliderOperatorAndValue,
      U21SliderProps<[number, number]>
    >;
  };
}

interface TextFilterOption {
  type: 'text';
  customize?: {
    [FILTER_OPERATOR.CONTAINS_TEXT]?: CustomizeOption<
      ContainsTextOperatorAndValue,
      U21TextFieldProps
    >;
  };
}

interface ExactTextFilterOption {
  type: 'exact_text';
  customize?: {
    [FILTER_OPERATOR.IS_EXACT_TEXT]?: CustomizeOption<
      IsExactTextOperatorAndValue,
      U21TextFieldProps
    >;
  };
}

interface CustomDataTextFilterOption {
  type: 'custom_data_text';
  customize?: {
    [FILTER_OPERATOR.IS_EXACT_TEXT]?: CustomizeOption<
      IsExactTextOperatorAndValue,
      U21TextFieldProps
    >;
    [FILTER_OPERATOR.IS_NOT_EXACT_TEXT]?: CustomizeOption<
      IsNotExactTextOperatorAndValue,
      U21TextFieldProps
    >;
    [FILTER_OPERATOR.IS_ONE_OF]?: CustomizeOption<
      IsOneOfOperatorAndValue,
      CreateableMultiSelectProps
    >;
    [FILTER_OPERATOR.IS_NOT_ONE_OF]?: CustomizeOption<
      IsOneOfOperatorAndValue,
      CreateableMultiSelectProps
    >;
  };
}

interface CustomDataNumberFilterOption {
  type: 'custom_data_number';
  customize?: {
    [FILTER_OPERATOR.IS_GREATER_THAN_NUMBER]?: CustomizeOption<
      IsGreaterNumberOperatorAndValue,
      U21TextFieldProps
    >;
    [FILTER_OPERATOR.IS_LESS_THAN_NUMBER]?: CustomizeOption<
      IsLesserNumberOperatorAndValue,
      U21TextFieldProps
    >;
  };
}

export type FilterOption = (
  | BooleanFilterOption
  | DateRangeFilterOption
  | MultiSelectFilterOption
  | NumberRangeFilterOption
  | SelectFilterOption
  | SliderRangeFilterOption
  | TextFilterOption
  | ExactTextFilterOption
  | CustomDataTextFilterOption
  | CustomDataNumberFilterOption
) &
  BaseFilterOption;

export type FilterOperatorAndValueBase =
  | ContainsTextOperatorAndValue
  | IsAfterDateOperatorAndValue
  | IsBeforeDateOperatorAndValue
  | IsBetweenDateOperatorAndValue
  | IsBetweenNumberOperatorAndValue
  | IsBetweenSliderOperatorAndValue
  | IsGreaterNumberOperatorAndValue
  | IsLesserNumberOperatorAndValue
  | IsOneOfOperatorAndValue
  | IsNotOneOfOperatorAndValue
  | IsOperatorAndValue
  | IsExactTextOperatorAndValue
  | IsNotExactTextOperatorAndValue
  | IsEmptyOperatorAndValue
  | IsNotEmptyOperatorAndValue
  | IsTrueOperatorAndValue
  | IsNotTrueOperatorAndValue;

export type FilterOperatorAndValue = DistributiveOmit<
  FilterOperatorAndValueBase,
  'onChange'
>;

export type FilterOperatorAndPartialValue = DistributivePartialBy<
  FilterOperatorAndValue,
  'value'
>;

export type Filter = FilterOperatorAndValue & { key: string };

export enum FilterContext {
  TRANSACTION_ANALYSIS = 'TRANSACTION_ANALYSIS',
  ALERTS = 'ALERTS',
  CASES = 'CASES',
  FINCEN_SAR = 'FINCEN_SAR',
  ENTITIES = 'ENTITIES',
  INSTRUMENTS = 'INSTRUMENTS',
  TRANSACTIONS = 'TRANSACTIONS',
  ACTION_EVENTS = 'ACTION_EVENTS',
  RULES = 'RULES',
  FLOW_NODES = 'FLOW_NODES',
  FLOWS = 'FLOWS',
}

export interface SavedFilter {
  id: number;
  title: string;
  description: string | null;
  filter_context: FilterContext;
  filters: Filter[];
  created_by_id: number;
  created_by_name: string;
  shared_team_ids: number[];
  created_at: string;
  updated_at: string;
}

export interface SaveFilterFormValues {
  title: string;
  description?: string;
  sharing: typeof SHARING_PRIVATE | typeof SHARING_TEAMS;
  team_ids: number[];
}
